import { CatalogAdoption } from './CatalogAdoption';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DestinationPicker } from './DestinationPicker';
import type { Candidate } from './AddSelection';
import { client } from '@frontend/features/common/services';
import { countries } from '@frontend/features/common/constants/countries';
import { CONFIG } from '@frontend/config';
import { catalogAdminKey } from './queries';
import { DiscoveryJob, DiscoveryPage, DiscoveryRequest, discoveryPending } from './discovery';
const root = 'hotel-catalog-admin/discoveries';
interface Account {
  connector_account_id: string;
  display_name: string;
  status: string;
  access_mode: string;
  access_codes: string[];
}
interface Connection {
  key: string;
  label: string;
  connector: 'hotelbeds' | 'travelgate';
  account: string;
  access?: string;
}
export function CatalogDiscovery({ active = true }: { active?: boolean }) {
  const { t, i18n } = useTranslation();
  const cache = useQueryClient();
  const [params, setParams] = useSearchParams();
  const jobId = params.get('discovery_job') || '';
  const [name, setName] = useState(params.get('q') || '');
  const pendingKey = `catalog-discovery-pending:${CONFIG.AGENCY_ID}`;
  const [pending, setPending] = useState<DiscoveryRequest | null>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(pendingKey) || 'null');
    } catch {
      return null;
    }
  });
  const [storageError, setStorageError] = useState(false);
  const [connection, setConnection] = useState(''),
    [country, setCountry] = useState(params.get('country_code') || '');
  const [destination, setDestination] = useState<Candidate | null>(() =>
    params.get('geo_place_id')
      ? {
        id: params.get('geo_place_id')!,
        label: params.get('destination_label') || params.get('geo_place_id')!,
        country_code: params.get('country_code'),
        locality: null,
      }
      : null,);
  const [offset, setOffset] = useState(0);
  const urlCountry = params.get('country_code') || '',
    urlDestination = params.get('geo_place_id') || '',
    urlDestinationLabel = params.get('destination_label') || '',
    urlName = params.get('q') || '';
  useEffect(() => {
    if (jobId) return;
    setCountry(urlCountry);
    setName(urlName);
    setDestination(
      urlDestination
        ? { id: urlDestination, label: urlDestinationLabel || urlDestination, country_code: urlCountry, locality: null }
        : null,
    );
  }, [jobId, urlCountry, urlDestination, urlDestinationLabel, urlName]);
  const showJob = (id: string, jobCountry: string, submittedParams = params) => {
    const next = new URLSearchParams(submittedParams);
    next.set('catalog_view', 'discovery');
    next.set('discovery_job', id);
    if (next.get('country_code') !== jobCountry) {
      next.delete('geo_place_id');
      next.delete('destination_label');
    }
    next.set('country_code', jobCountry);
    setParams(next);
  };
  const saving = useRef(false);
  const accounts = useQuery({
    queryKey: [...catalogAdminKey, 'discovery-connections'],
    enabled: active,
    retry: false,
    queryFn: async () => {
      const groups = await Promise.all(
        (['hotelbeds', 'travelgate'] as const).map(async (connector) => {
          const result = (
            await client.get<{ data: Account[] }>('hotel-catalog-admin/import-accounts', { params: { connector } })
          ).data;
          return result.data
            .filter(
              (a) => a.status === 'active' && a.access_mode === (connector === 'hotelbeds' ? 'direct' : 'include'),
            )
            .flatMap<Connection>((a) =>
              connector === 'hotelbeds'
                ? [
                  {
                    key: a.connector_account_id,
                    label: `${a.display_name} · Hotelbeds`,
                    connector,
                    account: a.connector_account_id,
                  },
                ]
                : a.access_codes.map((access) => ({
                  key: JSON.stringify([a.connector_account_id, access]),
                  label: `${a.display_name} · ${access} · Travelgate`,
                  connector,
                  account: a.connector_account_id,
                  access,
                })),);
        }),
      );
      return groups.flat() as Connection[];
    },
  });
  const selected = accounts.data?.find((c) => c.key === connection);
  const names = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language || 'es'], { type: 'region' });
    } catch {
      return null;
    }
  }, [i18n.language]);
  const countryOptions = useMemo(
    () =>
      countries
        .map((c) => ({ code: c.code, label: names?.of(c.code) || c.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [names],
  );
  const history = useQuery({
    queryKey: [...catalogAdminKey, 'discoveries', offset],
    enabled: active,
    retry: false,
    queryFn: async () => (await client.get<DiscoveryPage<DiscoveryJob>>(root, { params: { limit: 25, offset } })).data,
  });
  const create = useMutation({
    retry: false,
    mutationFn: async ({ request }: { request: DiscoveryRequest; searchParams: string }) =>
      (await client.post<{ data: DiscoveryJob }>(root, request)).data.data,
    onSuccess: (data, submission) => {
      try {
        sessionStorage.removeItem(pendingKey);
      } catch {
        /* The existing UUID is safe to replay. */
      }
      setPending(null);
      showJob(data.job_id, data.country_code, new URLSearchParams(submission.searchParams));
      setOffset(0);
      cache.invalidateQueries([...catalogAdminKey, 'discoveries']);
    },
    onSettled: () => {
      saving.current = false;
    },
  });
  const start = () => {
    if (saving.current || (!pending && (!selected || !/^[A-Z]{2}$/.test(country)))) return;
    const body = pending || {
      request_id: crypto.randomUUID(),
      connector_account_id: selected!.account,
      country_code: country,
      ...(selected!.access ? { access_code: selected!.access } : {}),
    };
    // Persist the UUID before dispatch so an uncertain response can be retried safely after reload.
    try {
      sessionStorage.setItem(pendingKey, JSON.stringify(body));
      setStorageError(false);
    } catch {
      setStorageError(true);
      return;
    }
    // Carry this submitted scope through the async response; the router setter
    // captured before navigation still sees the previous URL's filters.
    const next = new URLSearchParams(params);
    next.set('catalog_view', 'discovery');
    next.set('country_code', body.country_code);
    if (destination && destination.country_code === body.country_code) {
      next.set('geo_place_id', destination.id);
      next.set('destination_label', destination.label);
    } else {
      next.delete('geo_place_id');
      next.delete('destination_label');
    }
    if (name.trim()) next.set('q', name.trim());
    else next.delete('q');
    setParams(next);
    setPending(body);
    saving.current = true;
    create.mutate({ request: body, searchParams: next.toString() });
  };
  if (jobId) return (
    <DiscoveryResults
      key={jobId}
      jobId={jobId}
      active={active}
      onClose={() =>
        setParams((previous) => {
          const next = new URLSearchParams(previous);
          next.delete('discovery_job');
          return next;
        })
      }
    />
  );
  return (
    <Stack spacing={3} sx={{ '& input, & .MuiSelect-select': { fontSize: '16px !important' } }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h5">{t('hotelDiscovery.title')}</Typography>
        <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
          {t('hotelDiscovery.help')}
        </Typography>
        <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} alignItems="flex-start">
          <TextField
            select
            fullWidth
            label={t('hotelDiscovery.connection')}
            value={connection}
            disabled={!!pending || create.isLoading}
            onChange={(e) => {
              setConnection(e.target.value);
            }}
          >
            {(accounts.data || []).map((c) => (
              <MenuItem value={c.key} key={c.key}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            fullWidth
            options={countryOptions}
            value={countryOptions.find((c) => c.code === country) || null}
            disabled={!!pending || create.isLoading}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            onChange={(_, c) => {
              setCountry(c?.code || '');
              setDestination(null);
            }}
            renderInput={(params) => <TextField {...params} label={t('hotelDiscovery.country')} />}
          />
          <Box sx={{ width: '100%' }}>
            <DestinationPicker
              value={destination}
              disabled={!!pending || create.isLoading}
              onChange={(d) => {
                setDestination(d);
                if (d?.country_code) setCountry(d.country_code);
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {t('hotelIncorporation.wholeCountry')}
            </Typography>
          </Box>
        </Stack>
        <TextField
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          label={t('hotelIncorporation.optionalName')}
          value={name}
          inputProps={{ maxLength: 128 }}
          disabled={!!pending || create.isLoading}
          onChange={(e) => setName(e.target.value)}
        />
        {accounts.isError && (
          <Alert
            severity="error"
            action={<Button onClick={() => accounts.refetch()}>{t('hotelCatalogAdmin.reload')}</Button>}
          >
            {t('hotelCatalogAdmin.loadError')}
          </Alert>
        )}
        {accounts.data?.length === 0 && <Alert severity="info">{t('hotelDiscovery.noConnections')}</Alert>}
        {storageError && <Alert severity="error">{t('hotelDiscovery.storageError')}</Alert>}
        {pending && (
          <Alert severity={create.isError ? 'warning' : 'info'} sx={{ mt: 2 }}>
            {t('hotelDiscovery.pending', { country: pending.country_code, account: pending.connector_account_id })}
          </Alert>
        )}
        {pending && [400, 404, 409, 422].includes((create.error as any)?.response?.status) && (
          <Button
            onClick={() => {
              try {
                sessionStorage.removeItem(pendingKey);
                setPending(null);
                create.reset();
              } catch {
                setStorageError(true);
              }
            }}
          >
            {t('hotelDiscovery.correct')}
          </Button>
        )}
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          disabled={create.isLoading || (!pending && (!selected || !country))}
          onClick={start}
        >
          {t(pending ? 'hotelDiscovery.recover' : 'hotelDiscovery.start')}
        </Button>
      </Paper>
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('hotelDiscovery.history')}</Typography>
          <Button onClick={() => history.refetch()} disabled={history.isFetching}>
            {t('hotelCatalogAdmin.reload')}
          </Button>
        </Stack>
        {history.isError && <Alert severity="error">{t('hotelCatalogAdmin.loadError')}</Alert>}
        {history.isLoading && <LinearProgress />}
        {history.data?.data.length === 0 && (
          <Typography color="text.secondary">{t('hotelDiscovery.noRuns')}</Typography>
        )}
        <Stack spacing={1} sx={{ mt: 2 }}>
          {history.data?.data.map((j) => (
            <Paper variant="outlined" key={j.job_id} sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600}>
                    {names?.of(j.country_code) || j.country_code} · {j.account_name}
                    {j.access_code ? ` · ${j.access_code}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(j.created_at).toLocaleString(i18n.language)} ·{' '}
                    {t('hotelDiscovery.count', { count: j.hotels_processed })}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={t(`hotelDiscovery.status.${j.status}`)}
                  color={j.status === 'completed' ? 'success' : j.status === 'terminal_failed' ? 'error' : 'default'}
                />
                <Button onClick={() => showJob(j.job_id, j.country_code)}>{t('hotelDiscovery.view')}</Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button disabled={offset === 0 || history.isFetching} onClick={() => setOffset((v) => Math.max(0, v - 25))}>
            {t('hotelCatalogAdmin.previous')}
          </Button>
          <Button
            disabled={!history.data?.meta.has_more || history.isFetching || offset >= 10000000}
            onClick={() => setOffset((v) => v + 25)}
          >
            {t('hotelCatalogAdmin.next')}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
export function DiscoveryResults({ jobId, active, onClose }: { jobId: string; active: boolean; onClose(): void }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const job = useQuery({
    queryKey: [...catalogAdminKey, 'discovery', jobId],
    enabled: active,
    retry: false,
    queryFn: async () => (await client.get<{ data: DiscoveryJob }>(`${root}/${encodeURIComponent(jobId)}`)).data.data,
    refetchInterval: (d) => (discoveryPending(d?.status) && active ? 5000 : false),
    refetchIntervalInBackground: false,
  });
  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" gap={2}>
        <Box>
          <Typography variant="h5">{t('hotelIncorporation.title')}</Typography>
          {job.data && (
            <Typography color="text.secondary">
              {job.data.account_name} · {params.get('destination_label') || job.data.country_code}
            </Typography>
          )}
        </Box>
        <Button onClick={onClose}>{t('hotelIncorporation.newSearch')}</Button>
      </Stack>
      {job.isError && (
        <Alert severity="error" action={<Button onClick={() => job.refetch()}>{t('hotelCatalogAdmin.reload')}</Button>}>
          {t('hotelCatalogAdmin.loadError')}
        </Alert>
      )}
      {(!job.data || discoveryPending(job.data.status)) && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6">{t('hotelIncorporation.finding')}</Typography>
          <LinearProgress sx={{ my: 2 }} />
          {job.data && <Typography>{t('hotelDiscovery.count', { count: job.data.hotels_processed })}</Typography>}
          <Typography color="text.secondary">{t('hotelIncorporation.findingHelp')}</Typography>
        </Paper>
      )}
      {job.data?.status === 'terminal_failed' && <Alert severity="warning">{t('hotelDiscovery.failed')}</Alert>}
      {job.data?.status === 'completed' && (
        <CatalogAdoption
          discoveryId={jobId}
          active={active}
          country={job.data.country_code}
          initialFilters={{
            ...(params.get('q') ? { q: params.get('q')! } : {}),
            ...(params.get('geo_place_id') && params.get('country_code') === job.data.country_code
              ? { geo_place_id: params.get('geo_place_id')! }
              : {}),
          }}
          destinationLabel={params.get('destination_label') || ''}
        />
      )}
    </Stack>
  );
}
